// Where an identity went when a merge folded it into another one, for a view
// that is still on it (ADR-0042, ADR-0052, ADR-0053).
//
// A merge deletes the merged identity, and a view still holding its id — its own
// page, a selection in a list — would go on showing somebody who no longer
// exists. The view follows the survivor instead, one hop per merge, and learns
// of the merge in one of two ways:
//
// - A merge of this session built with `identityMergeMutation` — the face-match
//   merges — is read back from the mutation cache: the view moves at once, and
//   nothing is read to find out.
// - Any other merge — the duplicate review, a tattoo match, another analyst, one
//   made before a reload — is known to the server alone. A read of the identity
//   then answers 410 and names the survivor (`parseMergedRedirect`), and the
//   view that made the read passes it here.
import { useMutationState } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { parseMergedRedirect } from '@/features/entity-resolution/merged-redirect'
import { pathAfterMerge } from '../identity-path'

/** A merge as it was confirmed: who was folded away and who stays. */
export interface ConfirmedMerge {
  mergedId: string
  survivorId: string
}

/** The survivor a read of a merged identity named, and when it answered (ADR-0053). */
export interface NamedSurvivor {
  survivorId: string
  at: number
}

/** What a query's failed read of an identity named: the survivor of its 410, if it is one. */
export function survivorNamedBy(read: {
  error: unknown
  errorUpdatedAt: number
}): NamedSurvivor | null {
  const redirect = parseMergedRedirect(read.error)
  return redirect ? { survivorId: redirect.canonicalNodeId, at: read.errorUpdatedAt } : null
}

const identityMergeKey = (caseId: string) => ['case', caseId, 'identity-merge'] as const

/**
 * The key and the request of a mutation that merges two identities. The key is
 * handed out only here, so every mutation under it carries a `ConfirmedMerge` in
 * its variables — which is what `useMergedInto` reads back.
 */
export function identityMergeMutation<TMerge extends ConfirmedMerge, TResult>(
  caseId: string,
  mutationFn: (merge: TMerge) => Promise<TResult>
) {
  return { mutationKey: identityMergeKey(caseId), mutationFn }
}

/** What a view remembers of the identity it is on. */
export interface ViewStanding {
  personId: string | null
  /** Since when the view has been on it. */
  since: number
  /** The identities whose reads sent the view on, on its way here: oldest first. */
  left: readonly string[]
  /** The survivor a read of this identity named, while the view may follow it there. */
  sentTo: string | null
}

/** A view that has just opened on *personId*. */
export function standingOn(personId: string | null, now: number): ViewStanding {
  return { personId, since: now, left: [], sentTo: null }
}

/**
 * The view once it shows *personId*. Moved to the survivor a read named, it
 * keeps the trail it came by; moved anywhere else — the analyst picked another
 * identity, a merge of the session landed — it starts over.
 */
export function movedTo(before: ViewStanding, personId: string | null, now: number): ViewStanding {
  const followed = before.personId !== null && personId !== null && personId === before.sentTo
  return {
    ...standingOn(personId, now),
    left: followed ? [...before.left, before.personId] : [],
  }
}

/**
 * The survivor *named* that the view may move to, or null.
 *
 * Only an answer that came while the view was on the identity moves it: an
 * older one — a query cache keeps a failed read for minutes — is about whoever
 * held the id then, and the graph may have handed the freed id to another
 * identity since. And never back to an identity the view already left on its
 * way here: the merge records of a reused id can name each other.
 */
export function followable(standing: ViewStanding, named: NamedSurvivor | null): string | null {
  if (!named || named.at < standing.since) return null
  const { survivorId } = named
  if (survivorId === standing.personId || standing.left.includes(survivorId)) return null
  return survivorId
}

/** One toast for a chain of hops, not one per hop. */
const FOLLOWED_TOAST_ID = 'identity-merged-followed'

/**
 * The identity *personId* was merged into, as far as this view may follow it,
 * or null while it stands. *named* is what the view's own read of the identity
 * answered, if it answered that it was merged away.
 *
 * A view that got here by following what a read named says so once: the analyst
 * opened one identity and sees another, by a merge they did not just make.
 */
export function useMergedInto(
  caseId: string,
  personId: string | null,
  named: NamedSurvivor | null
): string | null {
  // The instance, not `t`: a language switch must not say it again.
  const { i18n } = useTranslation()
  // Since when the view has been on *personId*, and how it got there. A merge
  // older than that does not move it: the graph may hand a freed id out again,
  // to an identity created since, and that one stands.
  const [stored, setStored] = useState(() => standingOn(personId, Date.now()))
  let standing = stored.personId === personId ? stored : movedTo(stored, personId, Date.now())

  const merges = useMutationState({
    filters: { mutationKey: identityMergeKey(caseId), status: 'success' },
    select: ({ state }) => {
      const { mergedId, survivorId } = state.variables as ConfirmedMerge
      return { mergedId, survivorId, submittedAt: state.submittedAt }
    },
  })
  const merge = merges.find(
    item => item.mergedId === personId && item.submittedAt >= standing.since
  )
  // A merge of this session answers first: it costs no read.
  const sentTo = merge ? null : followable(standing, named)
  if (sentTo !== standing.sentTo) standing = { ...standing, sentTo }
  if (standing !== stored) setStored(standing)

  const cameFrom = standing.left.at(-1) ?? null
  useEffect(() => {
    if (cameFrom) toast.info(i18n.t('identity.mergedIntoThis'), { id: FOLLOWED_TOAST_ID })
  }, [cameFrom, i18n])

  return merge?.survivorId ?? sentTo
}

interface IdentityPageMerge {
  caseId: string
  identityId: string
  /** What one of the page's reads of the identity named: its detail, or its faces card. */
  named: NamedSurvivor | null
  /** How a host that embeds the page moves its selection to the survivor. */
  onMerged?: (survivorId: string) => void
}

/**
 * The identity page follows its identity into a merge. On the identity's own
 * page the URL moves to the survivor — search params, and with them an open
 * document overlay, and all. Embedded, the page hands the survivor to its host,
 * which holds the selection.
 *
 * True while the page is about to move: there is nothing of the merged identity
 * left to show in the meantime.
 */
export function useIdentityPageFollowsMerge({
  caseId,
  identityId,
  named,
  onMerged,
}: IdentityPageMerge): boolean {
  const mergedInto = useMergedInto(caseId, identityId, named)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const ownPage = mergedInto ? pathAfterMerge(pathname, caseId, identityId, mergedInto) : null

  useEffect(() => {
    if (!mergedInto) return
    if (ownPage) navigate({ pathname: ownPage, search }, { replace: true })
    else onMerged?.(mergedInto)
  }, [mergedInto, navigate, onMerged, ownPage, search])

  return Boolean(mergedInto && (ownPage || onMerged))
}
